// src/pages/purchaseOrder/components/PurchaseOrderDetailsModal.jsx
// Read-only quick view opened from the purchase order list's eye icon. Actions (receive, GRN,
// payments, edit) stay on the full details page, reached via "Open full details".
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, FileText, ShoppingCart, DollarSign, CreditCard, ExternalLink } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatCurrency } from '../../../utils/orderCalculations';
import { formatDate } from '../../../utils/dateUtils';
import PaymentHistoryList from '../../../components/common/PaymentHistoryList/PaymentHistoryList';
import ProductThumb from '../../procurement/components/ProductThumb';
import ps from '../../procurement/components/ProcurementDetails.module.css';
import shell from '../../offlineSales/components/OfflineSalesOrderDetailsModal.module.css';
import styles from './PurchaseOrderDetailsModal.module.css';

const itemLabel = (item) => {
  const attributes = (item.ProductVariation?.attributeValues || [])
    .map((av) => av.value)
    .filter(Boolean)
    .join(', ');
  return attributes ? `${item.Product?.name} (${attributes})` : item.Product?.name;
};

const PurchaseOrderDetailsModal = ({ purchaseOrder, statusInfo, onClose }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  if (!purchaseOrder) return null;

  const items = purchaseOrder.items || [];
  const payments = purchaseOrder.supplierPayments || [];

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={shell.modalOverlay} onClick={handleBackdropClick}>
      <div className={shell.modalContent}>
        <div className={shell.modalHeader}>
          <div className={shell.headerContent}>
            <div className={shell.headerTitle}>
              <Package size={24} />
              <div>
                <h2>{t('purchaseOrder.purchaseOrderDetails')}</h2>
                <p className={shell.orderNumber}>#{purchaseOrder.purchaseOrderNumber}</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className={shell.closeButton}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={shell.modalBody}>
          <div className={shell.section}>
            <h3 className={shell.sectionTitle}><FileText size={18} />{t('purchaseOrder.orderInformation')}</h3>
            <div className={ps.infoGrid}>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('purchaseOrder.status')}</span>
                <span className={ps.infoValue}>
                  {statusInfo ? (
                    <span
                      className={styles.statusBadge}
                      style={{ backgroundColor: statusInfo.bgColor, borderColor: statusInfo.color, color: statusInfo.color }}
                    >
                      {statusInfo.label}
                    </span>
                  ) : purchaseOrder.status}
                </span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('purchaseOrder.supplier')}</span>
                <span className={ps.infoValue}>{purchaseOrder.Supplier?.name || t('common.na')}</span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('purchaseOrder.warehouse')}</span>
                <span className={ps.infoValue}>{purchaseOrder.Warehouse?.name || t('common.na')}</span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('purchaseOrder.orderDate')}</span>
                <span className={ps.infoValue}>{formatDate(purchaseOrder.orderDate)}</span>
              </div>
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('purchaseOrder.expectedDeliveryDate')}</span>
                <span className={ps.infoValue}>
                  {purchaseOrder.expectedDeliveryDate ? formatDate(purchaseOrder.expectedDeliveryDate) : t('common.na')}
                </span>
              </div>
            </div>
          </div>

          <div className={shell.section}>
            <h3 className={shell.sectionTitle}><ShoppingCart size={18} />{t('purchaseOrder.purchaseOrderItems')}</h3>
            <div className={ps.tableWrapper}>
              <table className={ps.itemsTable}>
                <thead>
                  <tr>
                    <th>{t('purchaseOrder.itemProduct')}</th>
                    <th className={ps.alignRight}>{t('purchaseOrder.itemQuantity')}</th>
                    <th className={ps.alignRight}>{t('purchaseOrder.receivedQuantity')}</th>
                    <th className={ps.alignRight}>{t('purchaseOrder.itemUnitPrice')}</th>
                    <th className={ps.alignRight}>{t('purchaseOrder.itemLineTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className={ps.productCellInner}>
                          <ProductThumb item={item} alt={item.Product?.name || ''} />
                          <div className={ps.productCell}>
                            <span className={ps.productName}>{itemLabel(item)}</span>
                            {(item.ProductVariation?.sku || item.Product?.sku) && (
                              <span className={ps.productSku}>SKU: {item.ProductVariation?.sku || item.Product?.sku}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{parseFloat(item.quantity)}</td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{parseFloat(item.receivedQuantity || 0)}</td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(item.unitPrice)}</td>
                      <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{formatCurrency(item.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={shell.section}>
            <h3 className={shell.sectionTitle}><DollarSign size={18} />{t('purchaseOrder.financialSummary')}</h3>
            <div className={styles.totals}>
              <div className={styles.totalRow}>
                <span>{t('purchaseOrder.subtotal')}</span>
                <span>{formatCurrency(purchaseOrder.totalAmount)}</span>
              </div>
              {parseFloat(purchaseOrder.extraCharges) > 0 && (
                <div className={styles.totalRow}>
                  <span>{t('purchaseOrder.extraCharges')}</span>
                  <span>+{formatCurrency(purchaseOrder.extraCharges)}</span>
                </div>
              )}
              {parseFloat(purchaseOrder.discountAmount) > 0 && (
                <div className={styles.totalRow}>
                  <span>{t('purchaseOrder.discountAmount')}</span>
                  <span>-{formatCurrency(purchaseOrder.discountAmount)}</span>
                </div>
              )}
              <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
                <span>{t('purchaseOrder.grandTotal')}</span>
                <span>{formatCurrency(purchaseOrder.grandTotal)}</span>
              </div>
              <div className={styles.totalRow}>
                <span>{t('purchaseOrder.paymentAmount')}</span>
                <span>{formatCurrency(purchaseOrder.paidAmount)}</span>
              </div>
              <div className={styles.totalRow}>
                <span>{t('purchaseOrder.balanceAmount')}</span>
                <span>{formatCurrency(purchaseOrder.balanceAmount)}</span>
              </div>
            </div>
          </div>

          <div className={shell.section}>
            <h3 className={shell.sectionTitle}><CreditCard size={18} />{t('purchaseOrder.paymentRecords')}</h3>
            {payments.length > 0 ? (
              <PaymentHistoryList
                payments={payments.map((p) => ({
                  id: p.id,
                  iconFileName: p.paymentMethod?.iconFileName,
                  label: p.paymentMethod?.label,
                  amount: p.paymentAmount,
                  reference: p.reference,
                  date: p.paymentDate,
                  referenceTo: p.debitNote?.id ? `/procurement/debit-notes/${p.debitNote.id}` : null,
                }))}
              />
            ) : (
              <p className={styles.mutedText}>{t('purchaseOrder.noPaymentRecords')}</p>
            )}
          </div>
        </div>

        <div className={shell.modalFooter}>
          <button type="button" onClick={onClose} className={shell.cancelButton}>
            {t('common.close')}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/inventory/purchase-orders/${purchaseOrder.id}`)}
            className={shell.viewButton}
          >
            <ExternalLink size={16} />
            {t('purchaseOrder.openFullDetails')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDetailsModal;
